import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type AbilitiesService from 'ember-can/services/abilities';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { or } from 'ember-truth-helpers';
import { sourceToAbilityMap } from 'garaje/abilities/entry-approval';
import type { GetCanReviewOrReadBlocklistMatchModel } from 'garaje/abilities/security';
import TruncateWithTooltip from 'garaje/components/truncate-with-tooltip';
import dateFormat from 'garaje/helpers/date-format';
import type ScreenerReview from 'garaje/models/screener-review';
import type { GuestWithVisitApproval } from 'garaje/models/visit-approval';
import { getScreenerDisplayName } from 'garaje/utils/get-screener-display-name';

export interface VisitApprovalReviewHistorySignature {
  Args: {
    model: GuestWithVisitApproval;
  };
}

function getScreenerInfoTemplate(model: GuestWithVisitApproval, review: ScreenerReview) {
  if (review.isBlocklist()) {
    return <template>
      <span data-test-blocklist-history class="text-sm label">{{model.fullName}}
        matched someone on your blocklist.</span>
    </template>;
  } else if (review.isIdScanning()) {
    return <template>
      <span data-test-id-scanned class="text-sm label" data-test-id-scanned-invalid>
        {{review.idScanningFailureMessage}}
      </span>
    </template>;
  } else if (review.isPreRegistrationRequired()) {
    return <template>
      <span data-test-pre-registration-required-history class="text-sm label">Visitor was not invited.</span>
    </template>;
  } else if (review.isVisualCompliance()) {
    return <template>
      <span data-test-visual-compliance-history class="text-sm label">Matched at least one restricted party.</span>
    </template>;
  } else if (review.isOutgoingInvite()) {
    return <template>
      <span data-test-approvals-history class="text-sm label">Invite required approval</span>
    </template>;
  } else if (review.isPropertyInviteApproval()) {
    return <template>
      <span data-test-connect-approvals-history class="text-sm label">Property walk in required approval</span>
    </template>;
  } else if (review.isConditionalRule()) {
    return <template>
      <span data-test-conditional-rule-history class="text-sm label">
        Rule Triggered:
        <TruncateWithTooltip @value={{review.report.reason}} @length={{50}} @side="bottom" />
      </span>
    </template>;
  }

  return <template>{{getScreenerDisplayName review.screenerSource}} found</template>;
}

function getIcon(review: ScreenerReview): string {
  return review.action === 'approve' ? 'checkmark-green' : 'cross-small-red';
}

export default class VisitApprovalReviewHistory extends Component<VisitApprovalReviewHistorySignature> {
  @service declare abilities: AbilitiesService;

  get screenerReviews(): ScreenerReview[] {
    return this.args.model.visitApproval.screenerReviews.slice() || [];
  }

  @action
  getReviewText(review: ScreenerReview): string {
    return `${getScreenerDisplayName(review.screenerSource)} ${
      review.action === 'approve' ? 'approved' : 'denied'
    } by ${review.reviewerName}`;
  }

  @action
  checkCanReview(review: ScreenerReview): boolean {
    const source = sourceToAbilityMap[review.screenerSource] || review.screenerSource;
    return this.abilities.can(`review ${source}`, this.#permissionsModel);
  }

  @action
  checkCanView(review: ScreenerReview): boolean {
    const source = sourceToAbilityMap[review.screenerSource] || review.screenerSource;

    if (review.isBlocklist()) return this.abilities.can(`read blocklist for security`, this.#permissionsModel);
    return this.abilities.can(`view ${source}`, this.#permissionsModel);
  }

  get #permissionsModel(): GetCanReviewOrReadBlocklistMatchModel {
    return {
      context: 'location',
      parent: this.args.model,
      visitApproval: this.args.model.visitApproval,
    };
  }

  <template>
    <h3 class="label mt-1 mb-4">Approval History</h3>

    <div class="flex flex-col gap-y-3 pb-4 mb-8 border-b border-carbon-10" data-test-approval-history>
      {{#each this.screenerReviews as |review|}}
        <div class="flex flex-col">

          <div class="flex" data-test-review-row={{review.id}}>
            {{svgJar (getIcon review) height="16" width="16" class="flex-none mr-2"}}
            <div class="flex flex-col text-sm">
              {{#if (or (this.checkCanView review) (this.checkCanReview review))}}
                {{#let (getScreenerInfoTemplate @model review) as |ScreenerTemplate|}}
                  <ScreenerTemplate />
                {{/let}}
              {{/if}}
              <span data-test-review-details data-test-review-action={{review.action}}>
                {{this.getReviewText review}}
              </span>
              <span data-test-reviewed-at class="text-carbon-50">{{dateFormat review.reviewedAt "PP 'at' p"}}</span>
            </div>
          </div>
        </div>
      {{/each}}
    </div>
  </template>
}
